import React from 'react';
import { M } from '@dashboard-experience/mastodon';
import { t } from '../../lib/i18n/t';

interface Field {
  name: string;
  [key: string]: string;
}

const URL_PATTERN = /https?:\/\/[^\s<>"']+/;
// Sentence punctuation that follows a URL is not part of it, e.g. "...ssa-89.pdf, complete".
const TRAILING_PUNCTUATION = /[.,;:!?)\]}]+$/;

/**
 * Renders backend-provided text with any http(s) URLs turned into links.
 * Returns the original string when it contains no URL.
 */
export const linkifyText = (text: string): React.ReactNode => {
  if (!text) return text;

  const pattern = new RegExp(URL_PATTERN, 'g');
  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  let match = pattern.exec(text);

  while (match !== null) {
    const url = match[0].replace(TRAILING_PUNCTUATION, '');

    nodes.push(text.slice(cursor, match.index));
    nodes.push(
      <M.Link
        key={`${match.index}-${url}`}
        href={url}
        target='_blank'
        rel='noopener noreferrer'
        inline
      >
        {url}
      </M.Link>,
    );

    cursor = match.index + url.length;
    match = pattern.exec(text);
  }

  if (!nodes.length) return text;

  nodes.push(text.slice(cursor));

  return nodes;
};

export const bestAvailableDisplayText = (
  field: Field,
  displayName: string = '',
): string => {
  const translatedTitle: string = t(
    `components.Report.Verification.MatrixVerification.${field.name}.${displayName}`,
  );

  if (translatedTitle !== displayName) {
    return translatedTitle;
  }

  return field[displayName] || field.name;
};

export const bestAvailableDisplayTextForMvrConsent = (
  field: Field,
  displayName: string,
): string => {
  return t(
    `components.Report.Verification.MatrixVerification.mvr_consent_documentation_requested.${displayName}`,
  );
};
